import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SportsScoreIcon from '@mui/icons-material/SportsScore';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';

export const LIFECYCLE_STATUS = Object.freeze({
    pending: 'pending',
    started: 'started',
    ended: 'ended',
    finalized: 'finalized',
    archived: 'archived',
    locked: 'locked',
    legacy: 'legacy',
});

export const HEALTH_STATUS = Object.freeze({
    incomplete: 'incomplete',
    paused: 'paused',
    live_on_track: 'live_on_track',
    live_behind: 'live_behind',
    live_critical: 'live_critical',
    delivered_100: 'delivered_100',
    delivered_lt_100: 'delivered_lt_100',
    none: 'none',
});

// ===== Status (shared across entities) =====
export const LIFECYCLE_STATUS_MAP = {
    pending: { label: 'Pending', tone: 'neutral', variant: 'filled', Icon: ScheduleIcon },
    started: { label: 'Started', tone: 'info', variant: 'outlined', Icon: PlayArrowIcon },
    ended: { label: 'Ended', tone: 'neutral', variant: 'filled', Icon: CheckCircleOutlineIcon },

    finalized: {
        label: 'Finalized',
        tone: 'neutral',
        variant: 'filled',
        Icon: SportsScoreIcon,
        description: 'This campaign has ended and can no longer be extended.',
    },
    archived: {
        label: 'Archived',
        tone: 'neutral',
        variant: 'filled',
        Icon: null,
        description: 'This campaign can no longer be edited.',
    },
    locked: {
        label: 'Locked',
        tone: 'neutral',
        variant: 'filled',
        Icon: null,
        description: 'This campaign can no longer be edited or duplicated.',
    },
    legacy: {
        label: 'Legacy',
        tone: 'neutral',
        variant: 'filled',
        Icon: null,
        description:
            'This campaign can no longer be edited/duplicated. Detailed reports are no longer available.',
    },
};

// ===== Health (shared label texts) =====
export const HEALTH_STATUS_MAP = {
    incomplete: {
        label: 'Incomplete',
        tone: 'neutral',
        variant: 'filled',
        Icon: ReportProblemIcon,
    },
    paused: { label: 'Paused', tone: 'neutral', variant: 'filled', Icon: PauseIcon },
    live_on_track: {
        label: 'Live, On Track',
        tone: 'success',
        variant: 'filled',
        Icon: null,
    },
    live_behind: {
        label: 'Live, Behind',
        tone: 'warning',
        variant: 'filled',
        Icon: null,
    },
    live_critical: {
        label: 'Live, Critical',
        tone: 'error',
        variant: 'filled',
        Icon: ReportProblemIcon,
    },
    delivered_100: {
        label: '100% Delivered',
        tone: 'success',
        variant: 'filled',
        Icon: null,
    },
    delivered_lt_100: {
        label: '< 100% Delivered',
        tone: 'error',
        variant: 'filled',
        Icon: null,
    },
    none: { label: '', tone: 'neutral', variant: 'filled', Icon: ReportProblemIcon },
};

// ===== Valid health status per life cycle status (Campaign / Ad / Flight all use this) =====
export const VALID_HEALTH_BY_LIFECYCLE = Object.freeze({
    [LIFECYCLE_STATUS.Pending]: [HEALTH_STATUS.Incomplete],
    [LIFECYCLE_STATUS.Started]: [
        HEALTH_STATUS.Incomplete,
        HEALTH_STATUS.Paused,
        HEALTH_STATUS.LiveOnTrack,
        HEALTH_STATUS.LiveBehind,
        HEALTH_STATUS.LiveCritical,
    ],
    [LIFECYCLE_STATUS.Ended]: [HEALTH_STATUS.Delivered100, HEALTH_STATUS.DeliveredLt100],
    [LIFECYCLE_STATUS.Finalized]: [HEALTH_STATUS.Delivered100, HEALTH_STATUS.DeliveredLt100],
    [LIFECYCLE_STATUS.Archived]: [],
    [LIFECYCLE_STATUS.Locked]: [],
    [LIFECYCLE_STATUS.Legacy]: [],
});

// ===== Ad-specific label texts (same as campaign health but with "Ads") =====
export const AD_HEALTH_STATUS_LABEL_OVERRIDES = {
    incomplete: 'Incomplete Ads',
    paused: 'Paused Ads',
    live_on_track: 'Live, On Track Ads',
    live_behind: 'Live, Behind Ads',
    live_critical: 'Live, Critical Ads',
    delivered_100: '100% Delivered Ads',
    delivered_lt_100: '< 100% Delivered Ads',
};
